import React, { useCallback, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { BackContainer } from 'components/Common/BackContainer'
import BodyContainerWithHeader from 'components/Common/BodyContainerWithHeader'

import { DUPLICATE_KEY_ERROR } from 'constants/error'
import { CAR_SETTINGS_SUB_ROUTES } from 'constants/routes'
import { textFiles } from 'constants/textFiles'
import useNotification from 'hooks/useNotification'
import useTranslation from 'hooks/useTranslation'
import { Transmission } from 'models/car'
import {
  BaseIdEntity,
  GenericData,
  GenericInputVariable,
} from 'models/services/base'
import { CreateCarSettingGenericInputType } from 'models/services/CarSettings/creation'
import { PublicationStatus } from 'models/status'
import {
  TransmissionCreationAction,
  TransmissionCreationModel,
} from 'models/transmissionCreation'
import transmissionCreationReducer from 'reducers/transmissionCreationReducer'
import {
  createTransmissionStepperItems,
  INITIAL_TRANSMISSION_CREATION_STEPS as steps,
} from 'utils/CarSettings/Transmission/creation'
import { validateGraphQLErrorCode } from 'utils/error'
import { getTranslationsFilteredByLanguage } from 'utils/translation'

import { CREATE_TRANSMISSION } from 'graphQL/Operations/Transmission/Creation/mutations'

import { StyledBox } from 'styles/operation/creation'

const initialTransmissionInformationState: Transmission = {
  name: '',
  status: PublicationStatus.PUBLISHED,
  translations: [],
}

const initialData: TransmissionCreationModel = {
  transmissionInformation: initialTransmissionInformationState,
}

const TransmissionCreationPage = () => {
  const [currentStep, setCurrentStep] = useState<number>(0)

  const [transmissionData, dispatch] = useReducer(
    transmissionCreationReducer,
    initialData
  )

  const { transmissionInformation } = transmissionData
  const { translations } = transmissionInformation

  const history = useHistory()
  const { show } = useNotification()

  const { text } = useTranslation(textFiles.TRANSMISSION_CREATION)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const { stepper } = text

  const stepperItems = createTransmissionStepperItems(stepper)

  const updateTransmissionData = (action: TransmissionCreationAction) => {
    dispatch(action)
  }

  const handleBack = () => {
    if (currentStep - 1 >= 0) setCurrentStep((step) => step - 1)
  }
  const handleContinue = () => {
    setCurrentStep((step) => step + 1)
  }

  const [createTransmission, { loading: submitLoading }] = useMutation<
    GenericData<BaseIdEntity>,
    GenericInputVariable<CreateCarSettingGenericInputType>
  >(CREATE_TRANSMISSION, {
    onCompleted() {
      show({
        updatedSeverity: 'success',
      })
      history.push(CAR_SETTINGS_SUB_ROUTES.TRANSMISSION_LISTING)
    },
    onError(error) {
      const { errorExists } = validateGraphQLErrorCode(
        error,
        DUPLICATE_KEY_ERROR
      )
      if (errorExists) {
        show({
          updatedSeverity: 'error',
          message: generalText.notificationText.duplicateName,
        })
      } else
        show({
          updatedSeverity: 'error',
        })
    },
  })

  const handleSubmit = useCallback(() => {
    const { valueEn, valueEs } = getTranslationsFilteredByLanguage(translations)

    createTransmission({
      variables: {
        input: {
          name: transmissionInformation.name,
          name_translations: {
            en: valueEn ? valueEn.description : null,
            es: valueEs ? valueEs.description : null,
          },
          status: transmissionInformation.status
            ? PublicationStatus.PUBLISHED
            : PublicationStatus.UNPUBLISHED,
        },
      },
    })
  }, [transmissionInformation, createTransmission, translations])

  return (
    <StyledBox>
      <BackContainer />
      <BodyContainerWithHeader
        title={text.title}
        subtitle={text.processDescription}
        stepperItems={stepperItems}
        currentStep={currentStep}
      >
        {React.createElement(steps[currentStep], {
          handleContinue,
          handleBack,
          handleSubmit,
          submitLoading,
          transmissionData,
          updateTransmissionData,
        })}
      </BodyContainerWithHeader>
    </StyledBox>
  )
}

export default TransmissionCreationPage
